import React, { Component } from 'react';
import { HTTPService } from '../../../services/httpService';
import Autocomplete from 'react-google-autocomplete';

const API = new HTTPService();

class CustomerInfoStep extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // Customer data from API
      customer: null,
      loadingCustomer: true,
      loadError: null,

      // Edit mode
      isEditing: false,
      saving: false,
      saveError: null,

      // Editable fields
      name: '',
      phone: '',
      email: '',
      address: '',

      // Preserved fields (not displayed, needed for PUT)
      customerType: '',
      stripeCustomerId: ''
    };

    this.handleContinue = this.handleContinue.bind(this);
    this.handleSaveAndContinue = this.handleSaveAndContinue.bind(this);
  }

  async componentDidMount() {
    const customerId = this.props.request?.requestor;
    if (!customerId) {
      this.setState({ loadingCustomer: false, loadError: 'No customer ID found on this request.' });
      return;
    }

    try {
      const { data: customers } = await API.get(`/customers/${customerId}`);
      const cust = customers[0];
      if (!cust) {
        this.setState({ loadingCustomer: false, loadError: 'Customer record not found.' });
        return;
      }

      this.setState({
        customer: cust,
        name: cust.customer_name || '',
        phone: cust.customer_phone_number || '',
        email: cust.customer_Email_Address || '',
        address: cust.customer_address || '',
        customerType: cust.customer_type || '',
        stripeCustomerId: cust.stripe_customer_id || '',
        loadingCustomer: false
      });
    } catch (err) {
      console.error('Error fetching customer:', err);
      this.setState({ loadingCustomer: false, loadError: 'Failed to load customer information.' });
    }
  }

  toggleEdit = () => {
    this.setState({ isEditing: true, saveError: null });
  };

  cancelEdit = () => {
    const { customer } = this.state;
    this.setState({
      isEditing: false,
      saveError: null,
      name: customer.customer_name || '',
      phone: customer.customer_phone_number || '',
      email: customer.customer_Email_Address || '',
      address: customer.customer_address || ''
    });
  };

  handleFieldChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleAddress = (place) => {
    if (place.formatted_address) {
      this.setState({ address: place.formatted_address });
    }
  };

  handleContinue() {
    this.props.onCustomerInfoComplete();
  }

  async handleSaveAndContinue() {
    const { name, phone, email, address, customerType, stripeCustomerId, customer } = this.state;

    if (!name.trim()) {
      this.setState({ saveError: 'Customer name is required.' });
      return;
    }

    this.setState({ saving: true, saveError: null });

    try {
      const payload = {
        customerName: name.trim(),
        customerEmailAddress: email.trim(),
        customerPhoneNumber: phone.trim(),
        customerAddress: address.trim(),
        customerType: customerType,
        customerId: customer.customer_id,
        stripeCustomerId: stripeCustomerId
      };

      await API.put(`/customer/${customer.customer_id}`, payload);

      this.setState({ saving: false });
      this.props.onCustomerInfoComplete();
    } catch (err) {
      console.error('Error updating customer:', err);
      this.setState({ saving: false, saveError: 'Failed to save customer information. Please try again.' });
    }
  }

  renderReadOnly() {
    const { name, phone, email, address } = this.state;

    return (
      <>
        <div className="row">
          <div className="col-md-6 mb-3">
            <small className="text-muted d-block">Name</small>
            <span className="font-weight-bold">{name || '—'}</span>
          </div>
          <div className="col-md-6 mb-3">
            <small className="text-muted d-block">Phone</small>
            <span className="font-weight-bold">{phone || '—'}</span>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <small className="text-muted d-block">Email</small>
            <span className="font-weight-bold">{email || '—'}</span>
          </div>
          <div className="col-md-6 mb-3">
            <small className="text-muted d-block">Address</small>
            <span className="font-weight-bold">{address || '—'}</span>
          </div>
        </div>
      </>
    );
  }

  renderEditForm() {
    const { name, phone, email, address, saving } = this.state;
    const disabled = saving || this.props.loading;

    return (
      <>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="font-weight-bold" htmlFor="ci-name">Name *</label>
            <input
              id="ci-name"
              name="name"
              type="text"
              className="form-control"
              value={name}
              onChange={this.handleFieldChange}
              disabled={disabled}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="font-weight-bold" htmlFor="ci-phone">Phone</label>
            <input
              id="ci-phone"
              name="phone"
              type="tel"
              className="form-control"
              value={phone}
              onChange={this.handleFieldChange}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="font-weight-bold" htmlFor="ci-email">Email</label>
            <input
              id="ci-email"
              name="email"
              type="email"
              className="form-control"
              value={email}
              onChange={this.handleFieldChange}
              disabled={disabled}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="font-weight-bold" htmlFor="ci-address">Address</label>
            <Autocomplete
              apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
              style={{ width: '100%' }}
              className="form-control"
              onPlaceSelected={(place) => this.handleAddress(place)}
              options={{
                types: [],
                componentRestrictions: { country: 'us' }
              }}
              defaultValue={address}
              disabled={disabled}
            />
          </div>
        </div>
      </>
    );
  }

  render() {
    const { loadingCustomer, loadError, isEditing, saving, saveError } = this.state;
    const stepNumber = 3;

    return (
      <div className="customer-info-step">
        <h4 className="text-center mb-4">Step {stepNumber}: Customer Information</h4>

        {loadingCustomer && (
          <div className="text-center py-5">
            <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x text-primary"></i>
            <p className="mt-2 text-muted">Loading customer info...</p>
          </div>
        )}

        {loadError && (
          <div className="alert alert-warning">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            {loadError}
            <div className="mt-3 text-center">
              <button className="btn btn-primary" onClick={this.handleContinue}>
                Continue Without Verification
              </button>
            </div>
          </div>
        )}

        {!loadingCustomer && !loadError && (
          <>
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fa-sharp fa-regular fa-address-card mr-2"></i>
                      Customer Information
                    </h5>
                    {!isEditing && (
                      <button className="btn btn-sm btn-outline-light" onClick={this.toggleEdit}>
                        <i className="fa-sharp fa-regular fa-pen mr-1"></i> Edit
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {saveError && (
                      <div className="alert alert-danger mb-3">
                        <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
                        {saveError}
                      </div>
                    )}

                    {isEditing ? this.renderEditForm() : this.renderReadOnly()}

                    {isEditing && (
                      <div className="text-right mt-2">
                        <button className="btn btn-link text-secondary mr-2" onClick={this.cancelEdit}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-center text-muted mt-3">
                  <i className="fa-sharp fa-regular fa-circle-info mr-1"></i>
                  Please verify the customer's contact details are current.
                </p>
              </div>
            </div>

            {/* Action Button */}
            <div className="row mt-4">
              <div className="col-12 text-center">
                {isEditing ? (
                  <button
                    className="btn btn-success btn-lg"
                    onClick={this.handleSaveAndContinue}
                    disabled={saving || !this.state.name.trim() || this.props.loading}
                  >
                    {saving && (
                      <span className="mr-2">
                        <i className="fa-sharp fa-regular fa-refresh fa-spin" />
                      </span>
                    )}
                    SAVE & CONTINUE
                  </button>
                ) : (
                  <button
                    className="btn btn-success btn-lg"
                    onClick={this.handleContinue}
                    disabled={this.props.loading}
                  >
                    CONTINUE TO SIGNATURE
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default CustomerInfoStep;
